import { useEffect, useMemo, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorModalContext } from "../../../../components/AlertsModal/AlertsModal";
import { getSaleDetailService, refundSaleService, completeSaleService } from "../../../../services/sales";
import { getServiceError } from "../../../../utils/getServiceError";
import { SalesDetail, SaleStatus } from "../../../../interfaces/sales";
import { ORDER_STATUSES_IN_PREPARATION } from "../../../../interfaces/TableBills";
import { UseSaleReturn, CompleteSaleOptions } from "../interfaces";
import { buildSalePdfContent } from "../utils";
import { createPDFFromRows } from "../../../../utils/pdf";
import { formatDate } from "../../../../utils/dates";
import { SPEI_MERCHANTS } from "../../../../utils/sales";

export const useSale = (): UseSaleReturn => {
  const { uuid } = useParams();
  const [sale, setSale] = useState<SalesDetail | null>(null);
  const { showAlert } = useContext(ErrorModalContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingRefund, setIsLoadingRefund] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isConfirmingCash, setIsConfirmingCash] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState<boolean>(false);
  const [cancelPhoneNumber, setCancelPhoneNumber] = useState<string>('');
  const [cancelComment, setCancelComment] = useState<string>('');
  // Per-product restock decisions for the full-cancel flow. Defaults to `true` (regresar al stock) for every
  // product the moment the modal opens, matching the backend's default behavior when the flag is missing.
  const [cancelRestockDecisions, setCancelRestockDecisions] = useState<Record<string, boolean>>({});
  // YOS-10255: the extra "already in preparation" confirmation shown over the cancellation modal.
  const [showCancelPreparationConfirm, setShowCancelPreparationConfirm] = useState<boolean>(false);
  // YOSHI-10444: the sale detail PDF is generated in the browser, so the export icon waits for it.
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);

  const getData = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const response: SalesDetail = await getSaleDetailService(uuid || "");

      const formattedProducts = response.products.map((item) => {
        const parentProduct = item.uuid_parent
          ? response.products.find((p) => p.uuid === item.uuid_parent)?.product
          : null;

        return {
          ...item,
          discountName: item.uuid_parent ? "Complemento" : item.discountName,
          parentProduct,
        };
      });

      const sortedProducts = formattedProducts.sort((a, b) => {
        const getProductIndex = (product: typeof a): number =>
          formattedProducts.indexOf(product);

        const getParentIndex = (product: typeof a): number => {
          if (!product.uuid_parent) return -1;
          return formattedProducts.findIndex(
            (p) => p.uuid === product.uuid_parent,
          );
        };

        if (a.uuid_parent === b.uuid) return 1;
        if (b.uuid_parent === a.uuid) return -1;

        if (!a.uuid_parent && !b.uuid_parent)
          return getProductIndex(a) - getProductIndex(b);

        if (!a.uuid_parent && b.uuid_parent) {
          const bParentIndex = getParentIndex(b);
          return getProductIndex(a) - bParentIndex;
        }

        if (!b.uuid_parent && a.uuid_parent) {
          const aParentIndex = getParentIndex(a);
          return aParentIndex - getProductIndex(b);
        }

        if (a.uuid_parent && b.uuid_parent) {
          if (a.uuid_parent === b.uuid_parent)
            return getProductIndex(a) - getProductIndex(b);

          return getParentIndex(a) - getParentIndex(b);
        }

        return 0;
      });

      setSale({ ...response, products: sortedProducts });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  const cantRefund: string[] = useMemo(() => {
    const cantRefund: string[] = [];
    sale?.payments.forEach((item) => {
      if (item.status === 0) {
        cantRefund.push(item.code);
      }
    });

    return cantRefund;
  }, [sale]);

  const requiresSpei: boolean = useMemo(() => {
    return (sale?.payments ?? []).some(
      (p) => p.card_transaction && SPEI_MERCHANTS.includes(p.card_transaction.merchant?.toLowerCase() ?? '')
    );
  }, [sale]);

  const cardRefundTotal: number = useMemo(() => {
    const cardSubtotal: number = (sale?.payments ?? [])
      .filter((p) => p.card_transaction && SPEI_MERCHANTS.includes(p.card_transaction.merchant?.toLowerCase() ?? ''))
      .reduce((sum, p) => sum + (p.subtotal ?? 0), 0);
    // A reassigned tip left the card payment for another concession's sale; the cancellation refunds
    // the full amount the customer paid, so add it back to what the modal shows.
    return cardSubtotal + (sale?.reassigned_tip?.amount ?? 0);
  }, [sale]);

  /** Whether the sale has at least one cash payment. */
  const hasCashPayment: boolean = useMemo(() => {
    return (sale?.payments ?? []).some((p) => !!p.cash_transaction);
  }, [sale]);

  const makeRefund = async (): Promise<void> => {
    try {
      if (cantRefund.length > 0) {
        let codes = "";
        cantRefund.forEach((item) => {
          codes = `${codes}, ${item}`;
        });
        setShowRefundModal(false);
        return showAlert(
          `Las ventas con los códigos: ${codes} no están disponibles para reembolso, debido a su estado`,
          "error",
        );
      }
      setIsLoadingRefund(true);
      // Build the per-product restock array from the decisions map. The backend treats this as authoritative
      // ONLY when the request comes from Dashboard; we always send it and let the server decide.
      const arrProductsRestock = (sale?.products ?? []).map((p) => ({
        uuid_sale_product: p.uuid,
        restock: cancelRestockDecisions[p.uuid] !== false,
      }));
      const result = await refundSaleService(
        sale?.uuid || "",
        sale?.payments[0]?.card_transaction?.transaction_id || "",
        requiresSpei ? cancelPhoneNumber : undefined,
        cancelComment,
        arrProductsRestock,
      );
      setIsLoadingRefund(false);
      setShowRefundModal(false);
      if (result?.status === 'pending') {
        showAlert(
          "Solicitud de cancelación enviada. Un aprobador debe revisarla antes de ejecutarse.",
          "info",
          "Cancelación pendiente de aprobación",
        );
      } else {
        showAlert(
          `La venta ${sale?.code} ha sido cancelada`,
          "info",
          "Venta cancelada",
        );
        getData();
      }
    } catch (error) {
      setIsLoadingRefund(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  /**
   * YOS-10255: true when any product of the sale already went to the kitchen (order in Processing,
   * Ready or Completed), so cancelling the whole sale needs an explicit second confirmation.
   */
  const hasProductsInPreparation: boolean = useMemo(() => {
    return (sale?.products ?? []).some((product) =>
      ORDER_STATUSES_IN_PREPARATION.includes(product.order_status ?? -1),
    );
  }, [sale]);

  /**
   * YOS-10255: entry point of the "Confirmar cancelación" button of the cancellation modal. A sale with
   * products in preparation asks again; otherwise the cancellation runs as before.
   */
  const requestCancelSale = async (): Promise<void> => {
    if (hasProductsInPreparation) {
      setShowCancelPreparationConfirm(true);
      return;
    }
    await makeRefund();
  };

  /** YOS-10255: "Sí, cancelar" on the preparation confirmation. */
  const confirmCancelSaleInPreparation = async (): Promise<void> => {
    await makeRefund();
    setShowCancelPreparationConfirm(false);
  };

  /** YOS-10255: "No" on the preparation confirmation; nothing is cancelled. */
  const dismissCancelPreparationConfirm = (): void => {
    setShowCancelPreparationConfirm(false);
  };

  const completeSale = async (options?: CompleteSaleOptions): Promise<void> => {
    try {
      setIsCompleting(true);
      const payments = (options?.payments ?? []).map((p) => {
        return {
          subtotal: p.subtotal,
          tip: p.tip,
          payment_type: p.paymentType,
          merchant: p.merchant,
        };
      });
      await completeSaleService(sale?.uuid || "", { payments });
      setIsCompleting(false);
      showAlert(`La venta ${sale?.code} ha sido marcada como pagada`, "success", "Venta completada");
      getData();
    } catch (error) {
      setIsCompleting(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  /**
   * Confirms a cash payment for sales in "Por confirmar" status.
   * Reuses the complete sale endpoint; backend handles flow 3 when payments are already covered.
   */
  const confirmCashPayment = async (): Promise<void> => {
    try {
      setIsConfirmingCash(true);
      await completeSaleService(sale?.uuid || "", { payments: [] });
      setIsConfirmingCash(false);
      showAlert(
        `El pago en efectivo de la venta ${sale?.code} ha sido confirmado`,
        "success",
        "Pago confirmado",
      );
      getData();
    } catch (error) {
      setIsConfirmingCash(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  /**
   * YOSHI-10444: downloads the sale detail as a PDF with the same data the screen shows. The
   * template is built from the sale already in memory, so no extra request is needed.
   */
  const downloadPdf = async (): Promise<void> => {
    if (!sale) {
      return;
    }
    try {
      setIsDownloadingPdf(true);
      // Dashes, not slashes: the date is part of the downloaded file name.
      const fileName = `Venta_${sale.code}_${formatDate(
        new Date().toISOString(),
        "dd-MM-yyyy",
      )}`;
      await createPDFFromRows(fileName, buildSalePdfContent(sale), {
        pageMargins: [36, 36, 36, 36],
        defaultStyle: { font: "Inter", fontSize: 8 },
      });
      setIsDownloadingPdf(false);
    } catch (error) {
      setIsDownloadingPdf(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  useEffect(() => {
    getData();
  }, [uuid]);

  /**
   * Cambia la decisión de reinventario para un uuid_sale_product específico.
   * Lo invoca el modal de cancelación cuando el operador presiona Sí/No por producto.
   */
  const setCancelRestockDecision = (uuid: string, restock: boolean): void => {
    setCancelRestockDecisions((prev) => ({ ...prev, [uuid]: restock }));
  };

  return {
    cantRefund,
    isLoading,
    isLoadingRefund,
    hasProductsInPreparation,
    requestCancelSale,
    showCancelPreparationConfirm,
    confirmCancelSaleInPreparation,
    dismissCancelPreparationConfirm,
    isCompleting,
    isConfirmingCash,
    hasCashPayment,
    makeRefund,
    completeSale,
    confirmCashPayment,
    refreshData: getData,
    downloadPdf,
    isDownloadingPdf,
    sale,
    setShowRefundModal,
    showRefundModal,
    requiresSpei,
    cancelPhoneNumber,
    setCancelPhoneNumber,
    cancelComment,
    setCancelComment,
    cardRefundTotal,
    cancelRestockDecisions,
    setCancelRestockDecision,
  };
};
